import { Table as TableBase } from "@devexpress/dx-react-grid";
import React from "react";
import { IntlShape } from "react-intl";
import { TableRowActionProps } from "../../actions";
import { Identifiable } from "../../context";
import { Message } from "../../i18n";
import { Renderer } from "../../renderers";
import { FilterWidgetProps } from "../../widgets";

/**
 * Column renderer or configuration for each field in the table
 */
export type TableColumns<T> = {
  [P in keyof T]?: Renderer<T[P]> | TableColumnConfig<T[P]>;
};

/**
 * Filter definitions for table columns
 */
export type TableFilters<T, F> = {
  [P in keyof T & keyof F]?: TableFilter<T[P], F[P]>;
};

/**
 * Represents a table filter.
 *
 * Filters may be sync or async. Sync filters will filter out data based on a filter function locally, without
 * re-fetching data.
 *
 * Async filters will not filter current data, but attempt to reload the data by passing the filter values
 * to the onGetAll callback.
 *
 * T is the type of the field on the object to be filtered
 * F is the type that the filter widget returns.
 *
 * While it will be common that theses types are the same, it may not be the case (e.g. a date range filter)
 */
export type TableFilter<T, F> = {
  filterFn?: "async" | ((dataValue: T, filterValue: F, intl?: IntlShape) => boolean);
  widget: React.ComponentType<FilterWidgetProps<F>>;
  title?: Message;
  width?: number;
};

/**
 * Column configuration options
 */
type ColumnConfig<T> = {
  renderer?: Renderer<T>;
  width?: number;
  button?: (props: any) => React.ReactNode;
  align?: "left" | "center" | "right" | undefined;
};

/**
 * Table column configuration with optional nested children
 */
type TableColumnConfig<T> = ColumnConfig<T> & {
  children?: {
    [key: string]: ColumnConfig<T> & {
      renderer: Renderer<T>;
    };
  };
};

/**
 * Internationalization messages for the list component
 */
export interface ListMessages {
  error: Message;
  loading: Message;
  loadRetry: Message;
  loadError: Message;
  noData: Message;
  entityName: Message;
  tableActions: Message;
  reorderFailedTitle: Message;
  reorderFailedContent: Message;
  saveSuccess: Message;
  filtersTitle: Message;
  filter: Message;
  clearFilters: Message;
  export: Message;
  columns: Message;
  resultPerPage: Message;
  batchCancellation: Message;
  bulkUpdate: Message;
  tableHeaders?: { [key: string]: Message };
  tooltipHeaders?: { [key: string]: Message };
  import: Message;
  edit: Message;
}

/**
 * Pagination configuration for server-side pagination
 */
export interface PaginationConfig {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizes?: number[];
}

/**
 * Base props shared by all CrudListUncontrolled configurations
 */
interface CrudListUncontrolledBaseProps<T extends Identifiable, F = {}> {
  // Core Data (Required)
  /** Array of items to display in the table */
  items: T[];

  /** Loading state - shows loading spinner when true */
  isLoading: boolean;

  /** Error state - shows error message when provided */
  error?: Error | string | null;

  // Filters
  /** Current filter values managed by parent */
  filterValues?: Partial<F>;

  /** Callback when filter values change */
  onFilterChange?: (filters: Partial<F>) => void;

  /** Filter definitions for each column */
  filters?: TableFilters<T, F>;

  /** Whether filter accordion is closed by default */
  filtersClosedByDefault?: boolean;

  // Columns & Display (Required)
  /** Column definitions with renderers */
  columns: TableColumns<T>;

  /** Internationalization messages */
  messages?: ListMessages;

  /** Optional subheader text */
  subHeader?: string;

  /** Custom content rendered between the subheader and the filters accordion (e.g. a bespoke, real-time filter bar) */
  filterBar?: React.ReactNode;

  // Table Actions (Header)
  /** Render function for table-level actions (e.g., Create button) */
  actions?: () => React.ReactNode;

  // Row Actions
  /** Render function for row-level actions (e.g., Edit, Delete) */
  rowActions?: (props: TableRowActionProps<T>) => React.ReactNode;

  /** Width of the actions column in pixels */
  actionsWidth?: number;

  /** Tooltip content for row actions header */
  rowActionsTooltip?: () => React.ReactNode;

  /** Whether to fix the actions column on the right */
  fixedActions?: boolean;

  // Reordering (Drag & Drop)
  /** Callback for reordering rows via drag and drop */
  onReorder?: (entityId: T["id"], afterId: T["id"] | undefined) => Promise<void>;

  // Export
  /** Custom export callback - if not provided, uses default CSV export */
  onExport?: () => Promise<void> | void;

  /** Whether export operation is in progress */
  exportLoading?: boolean;

  /** Custom text for export button */
  onExportText?: string;

  /** Whether to hide the export button */
  hideExportButton?: boolean;

  // CSV Export
  /** Custom renderers for CSV export columns */
  CSVRenderers?: { [key: string]: (value: any, fields: any) => string };

  /** Name used for CSV filename generation */
  managerName?: string;

  // Column Visibility
  /** Whether to show column visibility toggle */
  columnVisibility?: boolean;

  /** Column visibility settings per column */
  columnVisibilityExtensions?: {
    columnName: string;
    togglingEnabled: boolean;
  }[];

  /** Initial list of hidden column names */
  initialHiddenColumnNames?: string[];

  // UI Configuration
  /** Fixed height for the table */
  listHeight?: number | string;

  /** Additional content to render in the header area */
  additionalHeaderContent?: React.ReactNode;

  /** Additional content to render in the footer area */
  additionalFooterContent?: React.ReactNode;

  /** Custom row component factory */
  customRow?: (items: T[]) => React.ComponentType<TableBase.DataRowProps>;
}

/**
 * Props when pagination is enabled (default behavior)
 */
interface CrudListWithPaginationProps<T extends Identifiable, F = {}>
  extends CrudListUncontrolledBaseProps<T, F> {
  hasPagination?: true;

  /** Pagination configuration and callbacks */
  pagination: PaginationConfig;
}

/**
 * Props when pagination is explicitly disabled
 */
interface CrudListWithoutPaginationProps<T extends Identifiable, F = {}>
  extends CrudListUncontrolledBaseProps<T, F> {
  hasPagination: false;

  /** Pagination configuration and callbacks */
  pagination?: never;
}

/**
 * Props interface for CrudListUncontrolled component
 */
export type CrudListUncontrolledProps<T extends Identifiable, F = {}> =
  | CrudListWithPaginationProps<T, F>
  | CrudListWithoutPaginationProps<T, F>;

/**
 * Type guard to check if column config has renderer or children
 */
export function isTableConfig<T>(
  config?: Renderer<T> | TableColumnConfig<T>
): config is TableColumnConfig<T> {
  const tableColumnConfig: TableColumnConfig<T> = config as TableColumnConfig<T>;
  return !!(tableColumnConfig.renderer || tableColumnConfig.children);
}
